import { useUserData } from 'Foundation/User/UserContext';
import { useEffect } from 'react';
import { useState } from 'react';
import { configureGoalManager } from '../GoalManager';
import { configureCDTManager } from '../CustomDataTagsManager';
import { LoadQualtrics, QualtricsHTMLLoad } from './QualtricsLoad';
import { getOmniChatUrl, getQualitricsId, isProd } from 'Foundation/Configurations/GlobalConfiguration';
import { IUserData } from 'Foundation/User/UserContextModels';
import { getChatUIPayloadPL, getChatUIPayloadSC } from './ChatUI';

// Content Square global value
let CS_CONF: any;

// This value is tied to qualtrics configuration. Must ask analytics team for this value
const qualtricsZoneId = getQualitricsId();

export const useAnalytics = () => {
  /* Currently Analytics.cshtml loads ensighten, heap, chat ui, twitter, linkedin, and facebook pixel*/

  const { userData } = useUserData();
  const window: IWindow = global.window;

  const setupHeap = () => {
    //heap

    if (userData && window.heap) {
      window.heap.removeEventProperty('EventState');
      window.heap.addEventProperties(
        !userData.isAgent()
          ? {
              EventUserID: userData.userId,
              EventUserType: userData.userType,
              EventState: userData.state,
              troux_uuid: 'F515B7C8-C672-4A2C-84BC-4B9182C2A982',
            }
          : {
              EventUserID: userData.userId,
              EventRole: userData.selectedStatCodeData.Role,
              EventTier: userData.selectedStatCodeData.Tier,
              EventStatCode: userData.selectedStatCodeData.Master,
              EventTrendingTier: userData.selectedStatCodeData.TrendingTier,
              EventStatCodeName: userData.selectedStatCodeData.AgencyName,
              EventSubStatCode: userData.selectedStatCodeData.SubStat,
              EventUserType: userData.userType,
              EventState: userData.state,
              EventName: userData.name,
              EventEmail: userData.email,
              'Current Page': window.document.title,
              troux_uuid: 'F515B7C8-C672-4A2C-84BC-4B9182C2A982',
            },
      );
      window.heap.identify(userData.userId);
      window.heap.addUserProperties(
        !userData.isAgent()
          ? {
              AgentUserID: userData.userId,
              UserType: userData.userType,
              AgentState: userData.state,
            }
          : {
              AgentUserID: userData.userId,
              Agentrole: userData.selectedStatCodeData.Role,
              AgentTier: userData.selectedStatCodeData.Tier,
              AgencyTrendingTier: userData.selectedStatCodeData.TrendingTier,
              AgentStatCode: userData.selectedStatCodeData.Master,
              AgencyStatCodeName: userData.selectedStatCodeData.AgencyName,
              AgentSubStatCode: userData.selectedStatCodeData.SubStat,
              UserType: userData.userType,
              AgentState: userData.state,
              AgentName: userData.name,
              AgentEmail: userData.email,
            },
      );
    }
  };

  const setupContentSquare = () => {
    window._uxa = window._uxa || [];
    try {
      if (userData && window._uxa) {
        window._uxa.push(['trackDynamicVariable', { key: 'userType', value: userData.userType.toLowerCase() }]);
        window._uxa.push(['trackDynamicVariable', { key: 'userId', value: userData.userIdNoSuffix().toLowerCase() }]);
      }
      if (typeof CS_CONF === 'undefined') {
        window._uxa.push(['setPath', window.location.pathname + window.location.hash.replace('#', '?__')]);
        var mt = document.createElement('script');
        mt.type = 'text/javascript';
        mt.async = true;
        mt.src = '//t.contentsquare.net/uxa/02f628e9362bd.js';
        document.getElementsByTagName('head')[0].appendChild(mt);
      } else {
        window._uxa.push(['trackPageview', window.location.pathname + window.location.hash.replace('#', '?__')]);
      }
    } catch (e) {}
  };

  const setupQualtrics = () => {
    if (userData?.userIdNoSuffix()) {
      // This value allows qualtrics to track feedback to a specific user and tie it to contentsquare
      sessionStorage.setItem('qualtrics_uid', userData.userIdNoSuffix().toLowerCase());
    }
    // This value allows qualtrics to suppress the survey for employees
    sessionStorage.setItem('userType', userData?.userType ?? '');
    // Qualtrics currently has two ways it loads on a page, feedback button on right side and when a user moves mouse towards url bar.
    // The feedback button is always shown and loads different configs for BL vs PL
    // The mouse hover towards url loads the PL and BL specific configs, but in production loads only 10% of the time vs 50% in non prod for testing
    LoadQualtrics(qualtricsZoneId);
  };

  const setupChatUI = async (userData: IUserData) => {
    try {
      if (userData) {
        let chatUIData = userData.isPL()
          ? getChatUIPayloadPL(userData)
          : userData.isBL()
            ? await getChatUIPayloadSC(userData)
            : null;
        if (chatUIData) {
          window.sendChatUiData(chatUIData);
        } else
          console.error(
            `Failure in useAnalytics.setupChatUI(): did not call sendChatUiData because chatUIData is: ${chatUIData}`,
          );
      }
    } catch (err: any) {
      console.error(`Error in useAnalytics.setupChatUI(): ${err.message}`);
    }
  };

  const [chatUIScriptLoaded, setChatUIScriptLoaded] = useState<boolean>(false);
  const [chatUIScriptLoadedCount, setChatUIScriptLoadedCount] = useState<number>(0);
  const [chatUIScriptAppended, setChatUIScriptAppended] = useState<boolean>(false);
  const [chatUIScriptAppendedCount, setChatUIScriptAppendedCount] = useState<number>(0);

  const loadChatUIScript = async (): Promise<boolean> => {
    try {
      const script: HTMLScriptElement = document.createElement('script');
      script.src = getOmniChatUrl();
      script.async = true;
      script.type = 'text/javascript';
      document.head.appendChild(script);
      return new Promise((resolve) => {
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
      });
    } catch (err) {
      console.error(err);
      return false;
    }
  };

  useEffect(() => {
    const loadScript = async () => {
      const scriptLoaded = await loadChatUIScript();
      if (scriptLoaded && window?.sendChatUiData && typeof window.sendChatUiData !== 'undefined') {
        setChatUIScriptAppended((a) => (a === true ? a : true));
        setChatUIScriptAppendedCount((c) => c + 1);
        if (userData) {
          setupChatUI(userData);
        }
      }
    };

    if (chatUIScriptLoaded) {
      console.log(`Chat UI script has already loaded this many times: ${chatUIScriptLoadedCount}`);
    } else if (chatUIScriptAppended) {
      console.log(`Chat UI script has already loaded this many times: ${chatUIScriptAppendedCount}`);
    } else if (window?.sendChatUiData && typeof window.sendChatUiData !== 'undefined') {
      setChatUIScriptLoaded((a) => (a === true ? a : true));
      setChatUIScriptLoadedCount((c) => c + 1);
      if (userData) {
        setupChatUI(userData);
      }
    } else {
      console.error(
        `useAnalytics.setupChatUI(): one of these is not defined: sendChatUiData: ${window?.sendChatUiData} \nOR\n userData: ${JSON.stringify(
          userData,
        )}`,
      );
      if (!chatUIScriptAppended) loadScript();
    }
  }, [
    window.sendChatUiData,
    userData,
    chatUIScriptLoaded,
    chatUIScriptLoadedCount,
    chatUIScriptAppended,
    chatUIScriptAppendedCount,
  ]);

  useEffect(() => {
    try {
      //heap
      if (window.heap) setupHeap();

      setupContentSquare();
      setupQualtrics(); // Leave this after contentsquare, to be able to connect CS to qualtrics

      configureGoalManager();
      configureCDTManager();
    } catch (ex) {
      console.error(ex);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export function LoadAnalyticsHTML() {
  return (
    <>
      <QualtricsHTMLLoad qualtricsZoneId={qualtricsZoneId} />
    </>
  );
}
